import { TAGS_LIST } from "../constants/Scenario";
import { IExerciserSDK } from "ap-exerciser-sdk";
import { VocabWord } from "../VocabModel";

export const getTagLabels = (tags: string[]) => {
  return TAGS_LIST.filter((tag) => tags?.includes(tag.value)).map(
    (tag) => tag.label
  );
};

export const saveToMyList = async (sdk: IExerciserSDK, scenarioId: string) => {
  const settings = await sdk.getDataManager().getSettings();
  let mylist: String[] = [];
  if (settings && settings.mylist && settings.mylist.length > 0) {
    mylist = Object.assign([], settings.mylist);
  }
  mylist = [...mylist, scenarioId];
  mylist = mylist.filter((c, index) => {
    return mylist.indexOf(c) === index;
  });
  const res = await sdk.getDataManager().saveSettings({
    data: {
      mylist,
    },
  });
};

export const chunkArray = (arr: any[], size: number) => {
  const chunkedArray = [];
  for (let i = 0; i < arr.length; i++) {
    const last = chunkedArray[chunkedArray.length - 1];
    if (!last || last.length === size) {
      chunkedArray.push([arr[i]]);
    } else {
      last.push(arr[i]);
    }
  }
  ;
  return chunkedArray;
}

export const binarySearch = <
  TKey extends keyof TValue,
  TValueType extends {
    localeCompare(t: TValueType, locales?: string | string[], options?: Intl.CollatorOptions): number
  } = string,
  TValue extends { [Key in TKey]: TValueType } = { [Key in TKey]: TValueType }
>
(orderedList: TValue[], key: TKey, value: TValueType): [TValue | null, number] => {
  let low: number = 0, high: number = orderedList.length - 1;
  let index = Math.round(low + high / 2);
  let compare: number = 0;
  let element: TValue;
  while (low <= high) {
    element = orderedList[index];

    compare = element[key].localeCompare(value)
    if (compare < 0) {//element before wanted value;
      low = index + 1;
    } else if (compare > 0) {//element after wanted value;
      high = index - 1;
    } else {
      return [element, index]
    }

    index = Math.floor((low + high) / 2);
  }

  return [null, -1];
};

export const binarySplice = <
  TKey extends keyof TValue,
  TValueType extends {
    localeCompare(t: TValueType, locales?: string | string[], options?: Intl.CollatorOptions): number
  } = string,
  TValue extends { [Key in TKey]: TValueType } = { [Key in TKey]: TValueType }
>(orderedList: TValue[], key: TKey, ...elements: TValue[]) => {
  const result: TValue[] = orderedList.slice();

  elements.forEach(element => {
    let [foundEl, index] = binarySearch(result, key, element[key])
    if (index >= 0) {
      result.splice(index, 1);
    }
  })

  return result;
}

export const resolveWordList = (wordIdList: Map<string, VocabWord[]>, scenarioWordIdList: {
  id: string
}[]): VocabWord[] => {
  if (wordIdList && wordIdList.size > 0) {
    const resolvedList: VocabWord[] = [];
    scenarioWordIdList.forEach(element => {
      const key = element.id[0];
      if (wordIdList.has(key)) {
        let [wordItem] = binarySearch(wordIdList.get(key)!, "id", element.id);
        if (wordItem) {
          resolvedList.push(wordItem);
        }
      }
    })
    return resolvedList;
  } else {
    return [];
  }
}

//Taken from https://stackoverflow.com/questions/27936772/how-to-deep-merge-instead-of-shallow-merge
/**
 * Simple object check.
 * @param item
 * @returns {boolean}
 */
export function isObject(item: any): item is { [key: string]: any } {
  return (item && typeof item === 'object' && !Array.isArray(item));
}

/**
 * Deep merge two objects.
 * @param target
 * @param sources
 */
export function mergeDeep(target: { [key: string]: any }, ...sources: { [key: string]: any }[]): object {
  if (!sources.length) return target;
  const source: { [key: string]: any } = sources.shift()!;

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key] || !isObject(target[key])) Object.assign(target, { [key]: {} });
        mergeDeep(target[key], source[key]);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }

  return mergeDeep(target, ...sources);
}

export function clearSelection() {
  if (window.getSelection) {
    if (window.getSelection()?.empty) {  // Chrome
      window.getSelection()!.empty();
    } else if (window.getSelection()?.removeAllRanges) {  // Firefox
      window.getSelection()!.removeAllRanges();
    }
  }
}

/**
 * Uses canvas.measureText to compute and return the width of the given text of given font in pixels.
 *
 * @param {String} text The text to be rendered.
 * @param {String} font The css font descriptor that text is to be rendered with (e.g. "bold 14px verdana").
 *
 * @see https://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
 */
interface getTextWidth {
  (text: string, font: string): number;

  canvas: HTMLCanvasElement | undefined
}

export const getTextWidth: getTextWidth = Object.assign((text: string, font: string) => {
  // re-use canvas object for better performance
  const canvas: HTMLCanvasElement = getTextWidth.canvas || (getTextWidth.canvas = document.createElement("canvas"));
  const context: CanvasRenderingContext2D | null = canvas.getContext("2d");
  if (context) {
    context.font = font;
    const metrics: TextMetrics = context.measureText(text);
    return metrics.width;
  }
  return -1;
}, { canvas: undefined })

export function getCssStyle(element: HTMLElement, prop: string) {
  return window.getComputedStyle(element, null).getPropertyValue(prop);
}

export function getCanvasFont(element: HTMLElement = document.body) {
  const fontWeight = getCssStyle(element, 'font-weight') || 'normal';
  const fontSize = getCssStyle(element, 'font-size') || '16px';
  const fontFamily = getCssStyle(element, 'font-family') || 'Times New Roman';

  return `${fontWeight} ${fontSize} ${fontFamily}`;
}